import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Redeem as PackageIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import StoreCustomerSectionCard from './StoreCustomerSectionCard';
import StoreCustomerServicePackageItem from './StoreCustomerServicePackageItem';
import ConfirmDialog from '../common/ConfirmDialog';
import SellServicePackageDialog from '../service-packages/SellServicePackageDialog';
import { getServicePackageErrorKey } from '../service-packages/servicePackageErrors';
import { getServicePackageName } from '../service-packages/servicePackageDisplay';
import { useToast } from '../../context/ToastContext';
import { allowsInStore } from '../../constants/CustomerPaymentMode';
import ServicePackageService, {
  CustomerServicePackageEffectiveStatus,
  isServicePackageActive,
  normalizeCustomerServicePackage,
} from '../../services/servicePackageService';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const CURRENT_STATUSES = [
  CustomerServicePackageEffectiveStatus.Active,
  CustomerServicePackageEffectiveStatus.PendingCheckout,
];

const PAST_STATUSES = [
  CustomerServicePackageEffectiveStatus.Exhausted,
  CustomerServicePackageEffectiveStatus.Expired,
  CustomerServicePackageEffectiveStatus.Canceled,
  CustomerServicePackageEffectiveStatus.Refunded,
];

const splitCustomerPackages = (customerPackages) => {
  const normalized = (customerPackages || []).map(normalizeCustomerServicePackage);
  return {
    current: normalized.filter((item) => CURRENT_STATUSES.includes(item.effectiveStatus)),
    past: normalized.filter((item) => PAST_STATUSES.includes(item.effectiveStatus)),
  };
};

const brandButtonSx = {
  textTransform: 'none',
  fontWeight: 700,
  color: BRAND.accentText,
  borderColor: BRAND.accent,
  borderRadius: '10px',
  whiteSpace: 'nowrap',
  '&:hover': { borderColor: BRAND.accentDark, backgroundColor: brandAlpha('accent', 0.08) },
};

const StoreCustomerServicePackagesCard = ({
  storeId,
  customerId,
  customerPackages,
  canSell,
  currencyCode,
  onChanged,
}) => {
  const { t, i18n } = useTranslation();
  const { showSuccess, showError } = useToast();
  const [managementData, setManagementData] = useState(null);
  const [sellOpen, setSellOpen] = useState(false);
  const [selling, setSelling] = useState(false);
  const [sellErrorKey, setSellErrorKey] = useState(null);
  const [packageToCancel, setPackageToCancel] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [showPast, setShowPast] = useState(false);

  const { current, past } = useMemo(() => splitCustomerPackages(customerPackages), [customerPackages]);

  useEffect(() => {
    if (!storeId || !canSell) {
      return undefined;
    }

    let cancelled = false;
    ServicePackageService.getServicePackageManagementData(storeId)
      .then((data) => {
        if (!cancelled) {
          setManagementData(data);
        }
      })
      .catch((loadError) => {
        console.error('Failed to load service packages for selling:', loadError);
      });

    return () => {
      cancelled = true;
    };
  }, [canSell, storeId]);

  const activePackages = useMemo(
    () => (managementData?.packages || []).filter((servicePackage) => isServicePackageActive(servicePackage.status)),
    [managementData],
  );
  const inStoreSalesEnabled = Boolean(managementData) && allowsInStore(managementData.servicePackageCustomerPaymentMode);
  const showSellButton = canSell && Boolean(managementData);

  const handleSell = async ({ packageId, checkout }) => {
    try {
      setSelling(true);
      setSellErrorKey(null);
      await ServicePackageService.sellInStore(storeId, { customerId, packageId, checkout });
      setSellOpen(false);
      showSuccess(t('servicePackagesManagement.sell.success'));
      await onChanged?.();
    } catch (sellError) {
      console.error('Failed to sell service package:', sellError);
      setSellErrorKey(getServicePackageErrorKey(sellError, 'servicePackagesManagement.sell.error'));
    } finally {
      setSelling(false);
    }
  };

  const handleCancelPackage = async () => {
    const target = packageToCancel;
    if (!target) {
      return;
    }

    try {
      setCancellingId(target.id);
      await ServicePackageService.cancelCustomerPackage(storeId, target.id);
      showSuccess(t('servicePackagesManagement.customer.cancel.success'));
      await onChanged?.();
    } catch (cancelError) {
      console.error('Failed to cancel customer service package:', cancelError);
      showError(t(getServicePackageErrorKey(cancelError, 'servicePackagesManagement.customer.cancel.error')));
    } finally {
      setCancellingId(null);
      setPackageToCancel(null);
    }
  };

  const sellButton = showSellButton ? (
    <Tooltip title={inStoreSalesEnabled ? '' : t('servicePackagesManagement.customer.sellUnavailable')}>
      <span>
        <Button
          id="customer-service-packages-sell"
          size="small"
          variant="outlined"
          startIcon={<PackageIcon fontSize="small" />}
          onClick={() => {
            setSellErrorKey(null);
            setSellOpen(true);
          }}
          disabled={!inStoreSalesEnabled}
          sx={brandButtonSx}
        >
          {t('servicePackagesManagement.customer.sell')}
        </Button>
      </span>
    </Tooltip>
  ) : null;

  return (
    <Box id="customer-service-packages">
      <StoreCustomerSectionCard
        title={t('servicePackagesManagement.customer.title')}
        icon={<PackageIcon fontSize="small" />}
        action={sellButton}
      >
        {current.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('servicePackagesManagement.customer.empty')}
          </Typography>
        ) : (
          <Box id="customer-service-packages-current">
            {current.map((customerPackage) => (
              <StoreCustomerServicePackageItem
                key={customerPackage.id}
                customerPackage={customerPackage}
                showStatus={customerPackage.effectiveStatus !== CustomerServicePackageEffectiveStatus.Active}
                canCancel={canSell && customerPackage.effectiveStatus === CustomerServicePackageEffectiveStatus.Active}
                cancelling={cancellingId === customerPackage.id}
                onCancel={setPackageToCancel}
              />
            ))}
          </Box>
        )}

        {past.length > 0 ? (
          <Box sx={{ mt: 1.5 }}>
            <Button
              id="customer-service-packages-toggle-past"
              size="small"
              onClick={() => setShowPast((value) => !value)}
              endIcon={showPast ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              aria-expanded={showPast}
              sx={{ textTransform: 'none', fontWeight: 600, color: BRAND.accentText, px: 0 }}
            >
              {t('servicePackagesManagement.customer.pastPackages', { count: past.length })}
            </Button>
            <Collapse in={showPast} unmountOnExit>
              <Box id="customer-service-packages-past">
                {past.map((customerPackage) => (
                  <StoreCustomerServicePackageItem
                    key={customerPackage.id}
                    customerPackage={customerPackage}
                    showStatus
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        ) : null}
      </StoreCustomerSectionCard>

      {showSellButton ? (
        <SellServicePackageDialog
          open={sellOpen}
          onClose={() => setSellOpen(false)}
          onSell={handleSell}
          selling={selling}
          activePackages={activePackages}
          services={managementData?.services || []}
          currencyCode={managementData?.currency || currencyCode}
          errorKey={sellErrorKey}
          storeId={storeId}
          customerId={customerId}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(packageToCancel)}
        onClose={() => setPackageToCancel(null)}
        onConfirm={handleCancelPackage}
        title={t('servicePackagesManagement.customer.cancel.title')}
        message={t('servicePackagesManagement.customer.cancel.message', {
          name: packageToCancel ? getServicePackageName(packageToCancel.name, i18n.language, t) : '',
        })}
        confirmText={t('servicePackagesManagement.customer.cancel.confirm')}
        cancelText={t('common.cancel')}
      />
    </Box>
  );
};

export default StoreCustomerServicePackagesCard;
